import { Component, EventEmitter, OnInit } from '@angular/core';
import { DocumentItemEditComponent, DocumentSetupResult } from '../../../models/document-item-edit-component.interface';
import { Observable, of } from 'rxjs';
import { DocumentDataUpdateInfo } from '../../../models/docuemnt-data-update-info.model';
import { DocumentCardData } from '../../../models/document-card-data.model';
import { DwMediaRepositoryService } from '@devwareapps/devware-cap';
import { DocumentsRepositoryService } from '../../../services/documents-repository.service';
import { AppMetaData, DocumentChangeLogTypeAllItems, DocumentFolderChangeLogEntity, DocumentFolderEntity, DocumentItemChangeLogEntity, DocumentItemEntity, DocumentItemStatusAllItems, DocumentOwnerTypeAllItems } from '../../../../../meta-data/app-meta-data.service';
import { map, mergeMap } from 'rxjs/operators';
import { SheetValidationResult } from '../../../models/sheet-validation-result.model';
import { AdminManagerService } from '../../../../admin/services/admin-manager.service';
import { DocumentsManagerService } from '../../../services/documents-manager.service';

@Component({
  selector: 'app-folder-edit',
  templateUrl: './folder-edit.component.html',
  styleUrls: ['./folder-edit.component.scss']
})
export class FolderEditComponent implements DocumentItemEditComponent, OnInit {
  selectedItem: DocumentFolderEntity;
  title: string;
  adding = false;

  rootFolderName: string = 'Main Folder';

  dataUpdated = new EventEmitter<DocumentDataUpdateInfo>();
  titleUpdated = new EventEmitter<string>();

  documentCardData: DocumentCardData;

  setupDefaults: DocumentSetupResult = {
    dialogTitle: 'Folder',
    editTitle: 'Want to edit the folder?',
    editMessage: 'Enable editing to change the folder name.',
    editActionText: 'Edit folder',
    startInEditMode: false,
    initialTitle: '',
    changeLog: []
  };


  constructor(
    protected dwMediaRepository: DwMediaRepositoryService,
    protected documentRespositoryService: DocumentsRepositoryService,
    protected documentsManagerService: DocumentsManagerService,
    protected adminManagerService: AdminManagerService
  ) { }


  ngOnInit(): void {

  }

  titleChanged() {
    this.titleUpdated.emit(this.selectedItem?.FolderName);
  }

  getItemName(): string {
    return this.selectedItem?.FolderName;
  }

  initDocumentData(documentCardData: DocumentCardData): Observable<DocumentSetupResult> {
    this.selectedItem = null;

    this.documentCardData = documentCardData;

    if (documentCardData.newItem) {
      return this.setupAddForm();
    }

    if (!documentCardData?.folderItem) {
      return of(null);
    }

    return this.documentRespositoryService.loadDocumentFolder(documentCardData.folderItem.DocumentFolderId).pipe(map(documentFolder => {
      this.selectedItem = documentFolder;

      const result: DocumentSetupResult = {
        dialogTitle: 'Folder',
        editTitle: 'Want to edit the folder?',
        editMessage: 'Enable editing to change the folder name.',
        editActionText: 'Edit folder',
        initialTitle: this.selectedItem?.FolderName || '',
        changeLog: documentFolder.DocumentFolderChangeLog || []
      };

      return result;
    }));
  }


    setupAddForm(): Observable<DocumentSetupResult> {
      const result: DocumentSetupResult = {
        ...this.setupDefaults,
        startInEditMode: true,
        initialTitle: 'New Folder',
        hideChangeLog: true,
        hideDelete: true,
        hideEditGate: true,
        hideChangeNotes: true,
        changeLog: []
      };
  
      this.adding = true;
      this.selectedItem = AppMetaData.DocumentFolder.CreateEntity();
  
      const aviatorUser = this.adminManagerService.getAviatorUser();
  
      this.selectedItem.FlightSchoolId = aviatorUser?.FlightSchoolId || null;
      this.selectedItem.DocumentItemStatusId = DocumentItemStatusAllItems.Active;
      this.selectedItem.ParentDocumentFolderId = this.documentCardData?.parentFolderId || null;
      this.selectedItem.DocumentGroupId = this.documentsManagerService.determineDocumentGroup();
      this.selectedItem.DocumentOwnershipTypeId = DocumentOwnerTypeAllItems.FlightSchool;
      this.selectedItem.FolderName = 'New Folder';
      
      return of(result);
    }

  validateSave(): SheetValidationResult {
    const validationResult: SheetValidationResult = {
      isValid: true
    }

    const messageParts: string[] = [];

    if (this.selectedItem.FolderName == null || this.selectedItem.FolderName.trim() === '') {
      messageParts.push('The folder must have a name.');
    }

    if (messageParts.length > 0) {
      validationResult.message = messageParts.join(' ');
      validationResult.isValid = false;
    }

    return validationResult;
  }

  deleteItemInternal(): Observable<DocumentDataUpdateInfo> {
    if (!this.selectedItem) return;

    this.selectedItem.DocumentItemStatusId = DocumentItemStatusAllItems.Inactive;

    return this.documentRespositoryService.saveDocumentFolder(this.selectedItem)
      .pipe(map((data) => {
        return { data: this.selectedItem, action: 'delete', DocumentCardData: this.documentCardData };
      }));
  }

  saveChangesInternal(changeDescription: string): Observable<DocumentDataUpdateInfo> {

    return this.buildChangeLog(this.selectedItem, false, changeDescription).pipe(
      mergeMap(itemToSave => this.documentRespositoryService.saveDocumentFolder(itemToSave))
    ).pipe(map((data) => {
      this.selectedItem = data;
      return { data: data, action: 'update', DocumentCardData: this.documentCardData };
    }));
  }

  protected buildChangeLog(documentFolderEntity: DocumentFolderEntity, isDelete: boolean, changeDescription: string): Observable<DocumentFolderEntity> {
    const itemToSave = { ...documentFolderEntity };

    const changeLog = AppMetaData.DocumentFolderChangeLog.CreateEntity();

    changeLog.DocumentFolderId = this.selectedItem.DocumentFolderId;
    changeLog.ChangeLogText = changeDescription;

     if (this.adding) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Renamed;

      changeLog.ChangeLogText = `Document Created`

      itemToSave.DocumentFolderChangeLog = [changeLog];

      return of(itemToSave);
    }

    if (isDelete) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Deleted;
      changeLog.ChangeLogText = `Folder Deleted`;

      itemToSave.DocumentFolderChangeLog = [changeLog];

      return of(itemToSave);
    }

    if (this.fieldChanged('FolderName')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Renamed;

      changeLog.ChangeLogText = `Renamed from"${this.documentCardData?.folderItem?.FolderName}" to "${this.selectedItem.FolderName}". ` + changeLog.ChangeLogText;

      itemToSave.DocumentFolderChangeLog = [changeLog];
    }

    if (this.fieldChanged('ParentDocumentFolderId')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Moved;

      return this.documentRespositoryService.getDocumentFoldersFromTo(itemToSave.DocumentGroupId, this.documentCardData?.documentItem?.ParentDocumentFolderId, this.selectedItem.ParentDocumentFolderId).pipe(map(folderNames => {
        changeLog.ChangeLogText = `Moved from ${folderNames}. ` + changeLog.ChangeLogText;

        itemToSave.DocumentFolderChangeLog = [...itemToSave.DocumentFolderChangeLog, changeLog];

        return itemToSave;
      }));
    }

    return of(itemToSave);
  }

  private fieldChanged(fieldName: keyof DocumentFolderEntity): boolean {
    const originalValue = this.documentCardData?.folderItem ? this.documentCardData.folderItem[fieldName] : null;
    const currentValue = this.selectedItem ? this.selectedItem[fieldName] : null;

    return originalValue !== currentValue;
  }

  hasChanges(): boolean {
    // New items always count as having changes
    if (this.adding) {
      return true;
    }

    return this.fieldChanged('FolderName') ||
           this.fieldChanged('ParentDocumentFolderId');
  }
}

